/**
 * Google Tag Manager (GTM) DataLayer utilities
 * Following official GTM documentation: https://developers.google.com/tag-platform/tag-manager/datalayer
 */

/* eslint-disable import/prefer-default-export */

import { getUUID } from './utils';

/**
 * Generates a unique event ID for each dataLayer push.
 * Uses crypto.randomUUID when available, falls back to a timestamp-based ID.
 */
const generateEventId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Pushes a select_deal event to the GTM dataLayer
 * @param {Object} dealData - The deal/reward data
 * @param {string} dealData.uid - The deal ID
 * @param {Object} dealData.store - The store/partner information
 * @param {string} dealData.store.title - The partner name
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 * @param {number} index - The position of the deal in the list (0-based)
 */
export const pushSelectDealEvent = (dealData, userData, index = 0) => {
  // Initialize dataLayer array if it doesn't exist (GTM best practice)
  window.dataLayer = window.dataLayer || [];

  // Clear previous ecommerce object (required for ecommerce events)
  window.dataLayer.push({ ecommerce: null });

  // Push the select_deal event
  window.dataLayer.push({
    event: 'select_deal',
    event_id: generateEventId(),
    user_id: getUUID(userData) || '',
    ecommerce: {
      item_list_name: 'Deal Browser',
      items: [{
        item_id: dealData?.uid || '',
        item_brand: dealData?.store?.title || '',
        index,
      }],
    },
  });
};

/**
 * Pushes a clicked_store event to the GTM dataLayer
 * @param {Object} store - The store data
 * @param {string} store.id - The store ID
 * @param {string} store.title - The store name
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 * @param {number} index - The position of the store in the list (0-based)
 */
export const pushSelectStoreEvent = (store, userData, index = 0) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({ ecommerce: null });

  window.dataLayer.push({
    event: 'clicked_store',
    event_id: generateEventId(),
    user_id: getUUID(userData) || '',
    ecommerce: {
      item_list_name: 'Store Browser',
      stores: [{
        store_id: store?.id || '',
        store_name: store?.title || '',
        index,
      }],
    },
  });
};

/**
 * Pushes a subscription_started event to the GTM dataLayer
 * Triggered when user clicks "GO UNLIMITED" button
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushSubscriptionStartedEvent = (userData) => {
  window.dataLayer = window.dataLayer || [];

  // Use normalized affiliate_cid (matches GA4, PostHog, backend)
  const userId = getUUID(userData) || '';

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'subscription_started',
    event_id: generateEventId(),
    user_id: userId,
  });
};

/**
 * Pushes a purchase event for subscription checkout to the GTM dataLayer.
 * event_id matches the Stripe checkout session ID so Meta CAPI browser + server dedup aligns.
 * @param {Object} params
 * @param {string} params.transactionId - Stripe checkout session ID
 * @param {number} params.value - Amount paid
 * @param {string} params.currency - ISO currency code
 * @param {string} params.itemName - Product line item name
 * @param {number} params.discount - Discount amount (0 if none)
 * @param {Object} params.userData - User data (affiliate_cid for tracking)
 * @param {string} params.email - User email for Meta Advanced Matching
 */
export const pushSubscriptionPurchaseEvent = ({
  transactionId,
  value,
  currency,
  itemName,
  discount,
  email,
  userData,
}) => {
  if (!transactionId) return;
  const eventId = transactionId;
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'purchase',
    event_id: eventId,
    user_id: getUUID(userData) || '',
    ecommerce: {
      transaction_id: transactionId,
      value,
      currency,
      items: [
        {
          item_id: transactionId,
          item_name: itemName,
          item_category: 'subscription',
          price: value,
          discount,
          quantity: 1,
        },
      ],
    },
    user_data: {
      email: email || '',
    },
  });
};

/**
 * Pushes a cancellation_started event to the GTM dataLayer
 * Triggered when user enters the cancel subscription flow
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushCancellationStartedEvent = (userData) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'cancellation_started',
    event_id: generateEventId(),
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a subscription_cancelled event to the GTM dataLayer
 * Triggered when the subscription cancellation is successfully completed
 * @param {Object} params
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 * @param {string} params.cancelReason - The radio-selected reason (e.g. 'Not saving enough')
 * @param {string} params.cancelComment - Optional free-text comment from the user
 */
export const pushSubscriptionCancelledEvent = ({ userData, cancelReason, cancelComment }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'subscription_cancelled',
    event_id: generateEventId(),
    cancel_reason: cancelReason || '',
    cancel_comment: cancelComment || '',
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a sign_up event to the GTM dataLayer
 * Triggered when a new user completes registration
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 * @param {string} method - The signup method ('email' | 'google' | 'apple')
 */
export const pushSignUpEvent = (userData, method) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'sign_up',
    event_id: generateEventId(),
    method: method || '',
    user_id: getUUID(userData) || '',
    trashiecash: userData?.balance?.currentBalance ?? 0,
  });
};

/**
 * Pushes a login event to the GTM dataLayer
 * Triggered when an existing user logs in
 * @param {Object} userData - The user data object (uses affiliate_cid for tracking)
 * @param {string} method - The login method ('email' | 'google' | 'apple')
 */
export const pushLoginEvent = (userData, method) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'login',
    event_id: generateEventId(),
    method: method || '',
    user_id: getUUID(userData) || '',
    trashiecash: userData?.balance?.currentBalance ?? 0,
  });
};

/**
 * Pushes a deal_redirect event to the GTM dataLayer
 * Triggered before redirecting the user to a partner site
 * @param {Object} params
 * @param {Object} params.rewardItem - The reward/deal data
 * @param {string} params.destinationUrl - The URL the user is being redirected to
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushDealRedirectEvent = ({ rewardItem, destinationUrl, userData }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'deal_redirect',
    event_id: generateEventId(),
    deal_id: rewardItem?.uid || '',
    partner_name: rewardItem?.store?.title || '',
    item_name: rewardItem?.name || '',
    item_category: rewardItem?.type || '',
    destination_url: destinationUrl || '',
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a coupon_revealed event to the GTM dataLayer
 * Triggered when a user redeems a deal/reward and the coupon is revealed
 * @param {Object} params
 * @param {Object} params.rewardItem - The reward/deal data
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushCouponRevealedEvent = ({ rewardItem, userData }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'coupon_revealed',
    event_id: generateEventId(),
    deal_id: rewardItem?.uid || '',
    partner_name: rewardItem?.store?.title || '',
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a gift_card_redeemed event to the GTM dataLayer
 * Triggered when a user successfully redeems a gift card
 * @param {Object} params
 * @param {string} params.giftCardName - The gift card store name
 * @param {number} params.giftCardValue - The gift card dollar value
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 */
export const pushGiftCardRedeemedEvent = ({ giftCardName, giftCardValue, userData }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'gift_card_redeemed',
    event_id: generateEventId(),
    gift_card: giftCardName || '',
    gift_card_value: giftCardValue || 0,
    user_id: getUUID(userData) || '',
  });
};

/**
 * Pushes a bag_registered event to the GTM dataLayer
 * Triggered when a user finalizes TBB registration
 * @param {Object} params
 * @param {string} params.bagId - The redemption code (unique bag identifier)
 * @param {string} params.bagType - The bag type (e.g., 'standard', 'kids', 'tech_box')
 * @param {Object} params.userData - The user data object (uses affiliate_cid for tracking)
 * @param {string} params.courier - The shipping courier name (e.g., 'FedEx', 'USPS')
 */
export const pushBagRegisteredEvent = ({ bagId, bagType, userData, courier }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'bag_registered',
    event_id: generateEventId(),
    bag_id: bagId || '',
    bag_type: bagType || '',
    user_id: getUUID(userData) || '',
    registration_date: new Date().toISOString().split('T')[0],
    courier: courier || '',
  });
};

/**
 * Pushes an account_drawer_viewed event to the GTM dataLayer.
 * Triggered when the My Account drawer becomes visible.
 */
export const pushAccountDrawerViewedEvent = () => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'account_drawer_viewed',
    event_id: generateEventId(),
  });
};

/**
 * Pushes a package_tracker_drawer_viewed event to the GTM dataLayer.
 * Triggered when the package tracker drawer is viewed for a stage.
 * @param {Object} params
 * @param {string|number} params.stage - Viewed stage (1-5 or 'lost')
 */
export const pushPackageTrackerDrawerViewedEvent = ({ stage }) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event: 'package_tracker_drawer_viewed',
    event_id: generateEventId(),
    stage: stage ?? '',
  });
};
